import React from "react";
import { useQuery } from "react-query";
import Alert from "@imminently/decisively-ui/Alert";
import EmptyStatus from "@imminently/decisively-ui/EmptyStatus";
import ErrorStatus from "@imminently/decisively-ui/ErrorStatus";
import LoadingStatus from "@imminently/decisively-ui/LoadingStatus";
import Icon from "@imminently/decisively-ui/Icon";
import Tabs from "@imminently/decisively-ui/Tabs";
import { JsonEditor } from "@/common/JsonEditor";
import type { ParsedRuleGraph } from "@imminently/decisively-core";
import { Contact, contactsService } from "@/app/providers/services";
import { useParsedGraph } from "@/pages/models/release/GraphContext";

const asString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str === "" ? null : str;
};

/**
 * Reads the ids of the contacts linked to a session, in the order the session lists them. The
 * full session object carries `contactLinks`, `contactIds` and `contactPaths` (see
 * deriveSessionContactFields in decisionapi). The legacy `contacts` field is still accepted,
 * either as an id array (db meta doc) or an id -> path map (decision API response).
 */
export const getSessionContactIds = (session: any): string[] => {
  const ids = new Set<string>();
  const add = (rawId: unknown) => {
    const id = asString(rawId);
    if (id) ids.add(id);
  };

  if (Array.isArray(session?.contactLinks)) {
    session.contactLinks.forEach((link: any) => add(link?.id ?? link?.contactId));
  }
  if (Array.isArray(session?.contactIds)) {
    session.contactIds.forEach(add);
  }
  if (session?.contactPaths && typeof session.contactPaths === "object" && !Array.isArray(session.contactPaths)) {
    Object.keys(session.contactPaths).forEach(add);
  }

  const legacy = session?.contacts;
  if (Array.isArray(legacy)) {
    legacy.forEach(add);
  } else if (legacy && typeof legacy === "object") {
    Object.keys(legacy).forEach(add);
  }

  return Array.from(ids);
};

/**
 * getList hands back the raw response body. An `id` filter returns a bare array (or a single
 * object when given one id) rather than the paged `{ data }` envelope, so accept all three.
 */
const toContactRecords = (body: unknown): Contact[] => {
  if (Array.isArray(body)) return body as Contact[];
  if (body && typeof body === "object") {
    const data = (body as { data?: unknown }).data;
    if (Array.isArray(data)) return data as Contact[];
    if ("id" in body) return [body as Contact];
  }
  return [];
};

const fetchContactRecords = async (ids: string[]): Promise<Contact[]> =>
  toContactRecords(await contactsService.getList({ id: ids }));

/** Fetches the contact records linked to a session, in getSessionContactIds order. No linked contacts gives []. */
export const fetchSessionContactRecords = async (session: unknown): Promise<Contact[]> => {
  const ids = getSessionContactIds(session);
  if (ids.length === 0) return [];
  const records = await fetchContactRecords(ids);
  const byId = new Map(records.map((record) => [String(record.id), record]));
  return ids.flatMap((id) => byId.get(id) ?? []);
};

const useContactRecords = (ids: string[]) => {
  const sortedIds = React.useMemo(() => [...ids].sort(), [ids]);

  return useQuery(
    ["sessionContactRecords", sortedIds],
    () => fetchContactRecords(sortedIds),
    { enabled: sortedIds.length > 0, refetchOnWindowFocus: false },
  );
};

const primitiveText = (value: unknown): string | null =>
  typeof value === "string" || typeof value === "number" ? asString(value) : null;

const contactLabel = (contact: Contact | undefined, fallback: string): string => {
  const name = [primitiveText(contact?.firstName), primitiveText(contact?.lastName)].filter(Boolean).join(" ");
  return name || fallback;
};

/**
 * Swaps attribute-id keys for the attribute's description by looking the key up in the session
 * graph, at any depth. Keys with no matching node (id, firstName, ...) are kept, as is a key whose
 * description is already taken on that object, so no value is lost to a collision.
 */
export const withAttributeDescriptions = (value: unknown, graph: ParsedRuleGraph | null): unknown => {
  if (Array.isArray(value)) return value.map(item => withAttributeDescriptions(item, graph));
  if (!value || typeof value !== "object") return value;

  const out: Record<string, unknown> = {};
  Object.entries(value).forEach(([key, child]) => {
    const node = graph?.node(key) ?? graph?.node(key.toLowerCase());
    const description = asString(node?.description);
    const outKey = description && !(description in out) && !(description in value) ? description : key;
    out[outKey] = withAttributeDescriptions(child, graph);
  });
  return out;
};

const ContactRecord = ({ id, record }: { id: string; record: Contact | undefined }) => {
  const graph = useParsedGraph();
  const json = React.useMemo(
    () => (record ? JSON.stringify(withAttributeDescriptions(record, graph), null, 2) : ""),
    [record, graph],
  );

  if (!record) {
    return (
      <EmptyStatus>
        <EmptyStatus.Title>Contact record not found</EmptyStatus.Title>
        <EmptyStatus.Content>No contact record exists with id {id}.</EmptyStatus.Content>
      </EmptyStatus>
    );
  }

  return (
    <JsonEditor
      className="flex-1 min-h-50"
      value={json}
      disabled
      withCopy
    />
  );
};

/**
 * Whether the release marks an entity as the contact and picks its contact ID attribute, which is
 * what the decision engine needs to link contacts to sessions. Mirrors getContactRel in
 * backend-commons: either the global entity (`meta.contact === "global"`) or a relationship with
 * `contactEntity: true`, each with a contact ID set.
 */
const hasContactConfig = (release: any): boolean => {
  if (release?.meta?.contact === "global") return Boolean(asString(release.meta.contactId));
  const rel = (release?.relationships ?? []).find((it: any) => it?.contactEntity === true);
  return Boolean(rel && asString(rel.contactId));
};

const ContactConfigMissing = ({ release, canOpenEntities }: { release: any; canOpenEntities: boolean }) => (
  <Alert
    variant="info"
    icon={<Icon name="info" />}
    className="mb-2"
    title="Contacts aren't configured for this release"
    description={
      <>
        Sessions are only linked to contacts once an entity is marked as the contact entity and given a contact ID
        attribute. Set this on the{" "}
        {canOpenEntities
          ? <a href="/build/entities" target="_blank" rel="noreferrer" className="underline">Entities page</a>
          : "Entities page"}
        {canOpenEntities ? "." : ` of release ${release?.releaseNo ?? ""} (select it in Build first).`}
        {" "}Once set, it only applies to new sessions. Existing sessions, including this one, won't be linked.
      </>
    }
  />
);

export type ContactsPanelProps = {
  session: any;
  /** The session's release. While it's loading the config check is skipped. */
  release?: any;
  /** Whether the session's release is the one selected in Build, so /build/entities opens it. */
  canOpenEntities?: boolean;
};

export const ContactsPanel: React.FC<ContactsPanelProps> = ({ session, release, canOpenEntities = false }) => {
  const ids = React.useMemo(() => getSessionContactIds(session), [session]);
  const configMissing = Boolean(release) && !hasContactConfig(release);
  const { data: records = [], isLoading, isError } = useContactRecords(ids);
  const recordsById = React.useMemo(() => new Map(records.map(it => [String(it.id), it])), [records]);

  const content = (() => {
    if (ids.length === 0) {
      if (configMissing) return <ContactConfigMissing release={release} canOpenEntities={canOpenEntities} />;
      return (
        <EmptyStatus>
          <EmptyStatus.Title>No contacts</EmptyStatus.Title>
          <EmptyStatus.Content>This session has no linked contacts.</EmptyStatus.Content>
        </EmptyStatus>
      );
    }
    if (isLoading) return <LoadingStatus message="Loading contacts..." />;
    if (isError) {
      return (
        <ErrorStatus>
          <ErrorStatus.Content>Failed to load the contact records.</ErrorStatus.Content>
        </ErrorStatus>
      );
    }
    return (
      <Tabs defaultValue={ids[0]} className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <Tabs.List className="shrink-0 border-b border-border mb-2">
          {ids.map(id => (
            <Tabs.Trigger key={id} value={id}>{contactLabel(recordsById.get(id), id)}</Tabs.Trigger>
          ))}
        </Tabs.List>
        {ids.map(id => (
          <Tabs.Content key={id} value={id} className="flex-1 min-h-0 overflow-auto data-[state=active]:flex flex-col">
            <ContactRecord id={id} record={recordsById.get(id)} />
          </Tabs.Content>
        ))}
      </Tabs>
    );
  })();

  return (
    <div className="flex flex-col flex-1 min-h-0 p-2">
      {/* Contacts can still be provided directly via the API, so show any we have under the notice. */}
      {configMissing && ids.length > 0 && <ContactConfigMissing release={release} canOpenEntities={canOpenEntities} />}
      {content}
    </div>
  );
};
